import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Form } from '@formio/react';
import { Formio } from 'formiojs';
import {
  Typography,
  Box,
  Paper,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import * as api from '../services/api';
import { ActionButton } from '../types/form';
import CollectionField from '../components/CollectionField';

// Import Formio and Quill CSS for WYSIWYG textarea editor
import 'formiojs/dist/formio.full.min.css';
import 'quill/dist/quill.snow.css';

// Register custom Formio components (fileupload)
// This must happen before any form is rendered
const registerCustomComponents = () => {
  // Access Formio's internal Components registry (not typed in @formio/react)
  const FormioLib = Formio as any;
  if (typeof FormioLib === 'undefined' || !FormioLib.Components) {
    console.warn('Formio not loaded, skipping custom component registration');
    return;
  }

  // Check if already registered
  if (FormioLib.Components.components.fileupload) {
    return;
  }

  const Field = FormioLib.Components.components.field;

  // Interactive File Upload Component
  // Supports drag-drop, multiple files, progress, download via signed URLs, and remove.
  // Reads config from Formio options: options.fileUpload = { apiBaseUrl, authToken, taskId }
  class FileUploadComponent extends Field {
    static schema(...extend: any[]) {
      return Field.schema({
        type: 'fileupload',
        label: 'File Upload',
        key: 'fileUpload',
        input: true,
        multiple: true
      }, ...extend);
    }

    static get builderInfo() {
      return {
        title: 'File Upload',
        icon: 'file',
        group: 'advanced',
        weight: 110,
        schema: FileUploadComponent.schema()
      };
    }

    get defaultSchema() {
      return FileUploadComponent.schema();
    }

    get uploadConfig() {
      const opts = this.options?.fileUpload || {};
      return {
        apiBaseUrl: opts.apiBaseUrl || '',
        authToken: opts.authToken || '',
        taskId: opts.taskId || ''
      };
    }

    formatFileSize(bytes: number) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    escapeHtml(text: string) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    renderFileRow(file: any, index: number) {
      const name = this.escapeHtml(file.originalFilename || file.name || 'Unknown');
      const size = this.formatFileSize(file.fileSize || file.size || 0);
      const downloadUrl = file.downloadUrl || file.url || '#';
      const isReadOnly = this.options?.readOnly || this.component?.disabled;
      return `
        <div class="fu-file-row" style="display:flex;justify-content:space-between;align-items:center;padding:10px 14px;background:#f8f9fa;border:1px solid #e9ecef;border-radius:4px;margin-bottom:6px;">
          <div style="min-width:0;flex:1;">
            <span style="font-weight:500;">${name}</span>
            <span style="font-size:12px;color:#6c757d;margin-left:8px;">${size}</span>
          </div>
          <div style="display:flex;gap:8px;align-items:center;flex-shrink:0;">
            <a href="${downloadUrl}" target="_blank" rel="noopener" style="color:#1976d2;text-decoration:none;font-size:13px;">Download</a>
            ${!isReadOnly ? `<button type="button" class="fu-remove-btn" data-index="${index}" style="background:none;border:none;color:#d32f2f;cursor:pointer;font-size:13px;padding:0;">Remove</button>` : ''}
          </div>
        </div>`;
    }

    render() {
      const isReadOnly = this.options?.readOnly || this.component?.disabled;
      return super.render(`
        <div ref="fileUploadRoot" class="formio-fileupload-interactive">
          ${!isReadOnly ? `
          <div ref="dropZone" class="fu-dropzone" style="border:2px dashed #ccc;border-radius:6px;padding:24px;text-align:center;cursor:pointer;transition:border-color 0.2s,background 0.2s;margin-bottom:8px;">
            <div style="color:#666;font-size:14px;">Drag &amp; drop files here or <span style="color:#1976d2;text-decoration:underline;">browse</span></div>
            <div style="color:#999;font-size:12px;margin-top:4px;">PDF, Word, JPEG, PNG, GIF &mdash; up to 10 MB each</div>
            <input ref="fileInput" type="file" multiple style="display:none;" />
          </div>
          <div ref="progressArea" style="display:none;margin-bottom:8px;">
            <div style="display:flex;align-items:center;gap:8px;">
              <div ref="progressBar" style="flex:1;height:6px;background:#e0e0e0;border-radius:3px;overflow:hidden;">
                <div ref="progressFill" style="height:100%;width:0%;background:#1976d2;transition:width 0.3s;"></div>
              </div>
              <span ref="progressText" style="font-size:12px;color:#666;">0%</span>
            </div>
          </div>` : ''}
          <div ref="fileList"></div>
          ${!isReadOnly ? `<div ref="errorArea" style="display:none;color:#d32f2f;font-size:13px;margin-top:4px;"></div>` : ''}
        </div>
      `);
    }

    attach(element: any) {
      const result = super.attach(element);

      this.loadRefs(element, {
        fileUploadRoot: 'single',
        dropZone: 'single',
        fileInput: 'single',
        fileList: 'single',
        progressArea: 'single',
        progressFill: 'single',
        progressText: 'single',
        errorArea: 'single'
      });

      this.renderFileList();

      if (this.options?.readOnly || this.component?.disabled) return result;

      const dropZone = (this.refs as any).dropZone;
      const fileInput = (this.refs as any).fileInput;

      if (dropZone && fileInput) {
        dropZone.addEventListener('click', () => fileInput.click());

        dropZone.addEventListener('dragover', (e: DragEvent) => {
          e.preventDefault();
          dropZone.style.borderColor = '#1976d2';
          dropZone.style.background = '#f0f7ff';
        });
        dropZone.addEventListener('dragleave', () => {
          dropZone.style.borderColor = '#ccc';
          dropZone.style.background = '';
        });
        dropZone.addEventListener('drop', (e: DragEvent) => {
          e.preventDefault();
          dropZone.style.borderColor = '#ccc';
          dropZone.style.background = '';
          if (e.dataTransfer?.files?.length) {
            this.handleFiles(Array.from(e.dataTransfer.files));
          }
        });

        fileInput.addEventListener('change', () => {
          if (fileInput.files?.length) {
            this.handleFiles(Array.from(fileInput.files));
            fileInput.value = '';
          }
        });
      }

      return result;
    }

    renderFileList() {
      const fileListEl = (this.refs as any).fileList;
      if (!fileListEl) return;
      const files = Array.isArray(this.dataValue) ? this.dataValue : [];
      if (files.length === 0) {
        fileListEl.innerHTML = '';
        return;
      }
      fileListEl.innerHTML = files.map((f: any, i: number) => this.renderFileRow(f, i)).join('');

      // Bind remove buttons
      fileListEl.querySelectorAll('.fu-remove-btn').forEach((btn: any) => {
        btn.addEventListener('click', () => {
          const idx = parseInt(btn.dataset.index, 10);
          this.removeFile(idx);
        });
      });
    }

    showError(msg: string) {
      const el = (this.refs as any).errorArea;
      if (!el) return;
      el.textContent = msg;
      el.style.display = 'block';
      setTimeout(() => { el.style.display = 'none'; }, 5000);
    }

    setProgress(pct: number) {
      const area = (this.refs as any).progressArea;
      const fill = (this.refs as any).progressFill;
      const text = (this.refs as any).progressText;
      if (!area) return;
      if (pct < 0) { area.style.display = 'none'; return; }
      area.style.display = 'block';
      if (fill) fill.style.width = pct + '%';
      if (text) text.textContent = Math.round(pct) + '%';
    }

    async handleFiles(fileList: File[]) {
      const { apiBaseUrl, authToken, taskId } = this.uploadConfig;
      const currentFiles = Array.isArray(this.dataValue) ? [...this.dataValue] : [];
      let uploaded = 0;

      this.setProgress(0);

      for (const file of fileList) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          if (taskId) formData.append('taskId', taskId);

          const headers: Record<string, string> = {};
          if (authToken) headers['Authorization'] = 'Bearer ' + authToken;

          const xhr = new XMLHttpRequest();
          const uploadResult: any = await new Promise((resolve, reject) => {
            xhr.upload.addEventListener('progress', (e) => {
              if (e.lengthComputable) {
                const filePct = (e.loaded / e.total) * 100;
                const overallPct = ((uploaded / fileList.length) * 100) + (filePct / fileList.length);
                this.setProgress(overallPct);
              }
            });
            xhr.addEventListener('load', () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                resolve(JSON.parse(xhr.responseText));
              } else {
                reject(new Error(xhr.statusText || 'Upload failed'));
              }
            });
            xhr.addEventListener('error', () => reject(new Error('Network error')));
            xhr.open('POST', apiBaseUrl + '/api/files/upload');
            Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
            xhr.send(formData);
          });

          if (uploadResult?.success && uploadResult.file) {
            currentFiles.push(uploadResult.file);
          }
        } catch (err: any) {
          this.showError(`Failed to upload ${file.name}: ${err.message}`);
        }
        uploaded++;
      }

      this.setProgress(-1);
      this.dataValue = currentFiles;
      this.updateValue(currentFiles, { modified: true });
      this.renderFileList();
    }

    async removeFile(index: number) {
      const files = Array.isArray(this.dataValue) ? [...this.dataValue] : [];
      const file = files[index];
      if (!file) return;

      // Delete from server
      if (file.id) {
        try {
          const { apiBaseUrl, authToken } = this.uploadConfig;
          const headers: Record<string, string> = { 'Content-Type': 'application/json' };
          if (authToken) headers['Authorization'] = 'Bearer ' + authToken;
          await fetch(apiBaseUrl + '/api/files/' + file.id, { method: 'DELETE', headers });
        } catch (err) {
          console.error('Failed to delete file from server:', err);
        }
      }

      files.splice(index, 1);
      this.dataValue = files;
      this.updateValue(files, { modified: true });
      this.renderFileList();
    }

    getValue() {
      return this.dataValue || [];
    }

    setValue(value: any, flags: any) {
      this.dataValue = Array.isArray(value) ? value : [];
      this.renderFileList();
      return super.setValue(value, flags);
    }

    get emptyValue() {
      return [];
    }
  }

  // Register the custom components
  FormioLib.Components.addComponent('fileupload', FileUploadComponent);
  console.log('Custom Formio components registered for FormRenderer: fileupload');
};

// Register custom components on module load
registerCustomComponents();

// Transform custom component types to standard Formio.js types at render time.
// This allows the builder to store a semantic type (e.g. 'richtexteditor') while
// the renderer uses the built-in textarea with Quill WYSIWYG — no custom
// component registration needed.
const transformComponents = (components: any[]): any[] =>
  components.map((comp: any) => {
    if (comp.type === 'richtexteditor' || comp.type === 'richtext') {
      return { ...comp, type: 'textarea', editor: 'quill', wysiwyg: true, rows: comp.rows || 6 };
    }
    return comp;
  });

const FormRenderer: React.FC = () => {
  const { formTemplateId } = useParams<{ formTemplateId: string }>();
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get('taskId');
  const navigate = useNavigate();

  const [formSchema, setFormSchema] = useState<any>(null);
  const [taskData, setTaskData] = useState<any>(null);
  const [formTemplate, setFormTemplate] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [actionButtons, setActionButtons] = useState<ActionButton[]>([]);
  const [currentFormData, setCurrentFormData] = useState<any>(null);
  const [allComponents, setAllComponents] = useState<any[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Helper function to get nested value from object using dot notation
  const getNestedValue = (obj: any, path: string): any => {
    if (!path || !obj) return undefined;

    // Handle array notation like "items[0].name" or "Addresses.values[0]"
    const normalizedPath = path
      .replace(/\[(\d+)\]/g, '.$1')  // Convert [0] to .0
      .split('.')
      .filter(key => key !== '');

    return normalizedPath.reduce((current, key) => {
      return current?.[key];
    }, obj);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Claim the task first if taskId is provided
        if (taskId) {
          try {
            await api.claimSpecificTask(taskId, 'user-123');
          } catch (claimErr: any) {
            // If claim fails, check if task is already claimed by this user
            console.log('Claim attempt failed, checking task status:', claimErr);
            const taskCheckResponse = await api.getTask(taskId);
            const existingTask = taskCheckResponse.data;

            // If task is claimed by someone else or not found, show error
            if (existingTask.claimedBy && existingTask.claimedBy !== 'user-123') {
              throw new Error(`Task is already claimed by ${existingTask.claimedBy}`);
            }
            // If task is claimed by current user or pending, continue
            console.log('Task is available or already claimed by current user, continuing...');
          }
        }

        // Fetch task data if taskId is provided
        if (taskId) {
          const taskResponse = await api.getTask(taskId);
          const task = taskResponse.data;

          setTaskData(task.taskData || {});

          // Use snapshotted form schema from task if available
          if (task.formSchema) {
            console.log('Using snapshotted form schema from task');

            // Extract action buttons from schema
            if (task.formSchema.actionButtons) {
              setActionButtons(task.formSchema.actionButtons);
            }

            // Separate collection fields from regular Formio fields
            const components = task.formSchema.components || [];
            const regularComponents = transformComponents(components.filter((c: any) => c.type !== 'collection' && c.type !== 'button'));

            setAllComponents(components);

            // Set schema without action buttons and collection fields (Formio doesn't understand them)
            const { actionButtons: _, ...formioSchema } = task.formSchema;
            formioSchema.components = regularComponents;
            setFormSchema(formioSchema);

            // Set template info for display
            setFormTemplate({
              name: task.metadata?.formTemplateName || 'Form',
              description: task.metadata?.formTemplateDescription || ''
            });
          } else {
            // Fallback: fetch form template if no snapshot exists (backwards compatibility)
            console.log('No snapshotted schema found, fetching form template');
            const templateResponse = await api.getFormTemplate(formTemplateId!);
            const template = templateResponse.data;
            setFormTemplate(template);

            // Extract action buttons from schema
            if (template.schema?.actionButtons) {
              setActionButtons(template.schema.actionButtons);
            }

            // Separate collection fields from regular Formio fields
            const components = template.schema?.components || [];
            const regularComponents = transformComponents(components.filter((c: any) => c.type !== 'collection' && c.type !== 'button'));

            setAllComponents(components);

            // Set schema without action buttons and collection fields (Formio doesn't understand them)
            const { actionButtons: _, ...formioSchema } = template.schema;
            formioSchema.components = regularComponents;
            setFormSchema(formioSchema);
          }
        } else {
          // No task ID - just preview the form template
          const templateResponse = await api.getFormTemplate(formTemplateId!);
          const template = templateResponse.data;
          setFormTemplate(template);

          // Extract action buttons from schema
          if (template.schema?.actionButtons) {
            setActionButtons(template.schema.actionButtons);
          }

          // Separate collection fields from regular Formio fields
          const components = template.schema?.components || [];
          const regularComponents = transformComponents(components.filter((c: any) => c.type !== 'collection' && c.type !== 'button'));

          setAllComponents(components);

          // Set schema without action buttons and collection fields (Formio doesn't understand them)
          const { actionButtons: _, ...formioSchema } = template.schema;
          formioSchema.components = regularComponents;
          setFormSchema(formioSchema);
        }
      } catch (err: any) {
        console.error('Error fetching form data:', err);
        setError('Failed to load form: ' + (err.response?.data?.error || err.message));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [formTemplateId, taskId]);

  const handleSubmit = async (submission: any) => {
    if (!taskId) {
      setError('No task ID provided');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Submit the form data (selectedAction is already included in submission.data)
      await api.submitForm(taskId, formTemplateId!, submission.data, 'user-123');

      // Navigate back to tasks page
      alert('Form submitted successfully!');
      navigate('/tasks');
    } catch (err: any) {
      console.error('Error submitting form:', err);
      setError('Failed to submit form: ' + (err.response?.data?.error || err.message));
      setSubmitting(false);
    }
  };

  const validateForm = (): boolean => {
    // Get the current form data (either from state or taskData)
    const data = currentFormData || taskData || {};

    // Check all components for required fields
    const missingFields: string[] = [];

    allComponents.forEach((component: any) => {
      // Skip non-input components (like collection fields)
      if (component.type === 'collection') return;

      // Check if field is required
      if (component.validate?.required) {
        const value = data[component.key];

        // Check if value is missing or empty
        if (value === undefined || value === null || value === '') {
          missingFields.push(component.label || component.key);
        }
      }
    });

    if (missingFields.length > 0) {
      setValidationError(`Please fill in the following required fields: ${missingFields.join(', ')}`);
      return false;
    }

    setValidationError(null);
    return true;
  };

  const handleActionButtonClick = async (action: string) => {
    // Validate form before submitting
    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }

    // Set selected action and submit with current form data
    const submissionData = {
      ...(currentFormData || taskData || {}),
      selectedAction: action
    };

    await handleSubmit({ data: submissionData });
  };

  const handleSaveAndClose = async () => {
    if (!taskId) {
      setError('No task ID provided');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Save the current form data as a draft (no validation required)
      const draftData = currentFormData || taskData || {};
      await api.saveDraft(taskId, draftData);

      // Navigate back to tasks page
      navigate('/tasks');
    } catch (err: any) {
      console.error('Error saving draft:', err);
      setError('Failed to save draft: ' + (err.response?.data?.error || err.message));
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Just navigate back without releasing the task
    navigate('/tasks');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/tasks')}
          sx={{ mb: 2 }}
        >
          Back to Tasks
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/tasks')}
        sx={{ mb: 2 }}
      >
        Back to Tasks
      </Button>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          {formTemplate?.name || 'Form'}
        </Typography>
        {formTemplate?.description && (
          <Typography variant="body2" color="text.secondary" paragraph>
            {formTemplate.description}
          </Typography>
        )}

        {formSchema && (
          <>
            {/* Render components in order, grouping consecutive non-collection fields */}
            {(() => {
              const groups: Array<{ type: 'formio' | 'collection', components?: any[], component?: any }> = [];
              let currentGroup: any[] = [];

              allComponents.forEach((comp, idx) => {
                if (comp.type === 'collection') {
                  // If we have accumulated non-collection fields, add them as a group
                  if (currentGroup.length > 0) {
                    groups.push({ type: 'formio', components: currentGroup });
                    currentGroup = [];
                  }
                  // Add the collection field
                  groups.push({ type: 'collection', component: comp });
                } else {
                  // Accumulate non-collection fields
                  currentGroup.push(comp);
                }
              });

              // Add any remaining non-collection fields
              if (currentGroup.length > 0) {
                groups.push({ type: 'formio', components: currentGroup });
              }

              return groups.map((group, groupIdx) => {
                if (group.type === 'collection') {
                  const fieldData = getNestedValue(taskData, group.component.key);
                  console.log('Collection field:', group.component.key, 'Data:', fieldData, 'Full taskData:', taskData);
                  return (
                    <CollectionField
                      key={`collection-${groupIdx}`}
                      label={group.component.label}
                      data={fieldData}
                    />
                  );
                } else {
                  // Render a mini Formio form with this group of components
                  const miniSchema = {
                    display: 'form',
                    components: group.components
                  };
                  return (
                    <Box
                      key={`formio-${groupIdx}`}
                      sx={{
                        '& .formio-form': {
                          fontFamily: 'inherit',
                        },
                        '& .form-group': {
                          marginBottom: '24px',
                        },
                        '& label': {
                          fontWeight: 500,
                          fontSize: '0.875rem',
                          marginBottom: '8px',
                          display: 'block',
                          color: 'rgba(0, 0, 0, 0.87)',
                        },
                        '& .form-control': {
                          width: '100%',
                          padding: '16.5px 14px',
                          fontSize: '1rem',
                          fontFamily: 'inherit',
                          borderRadius: '4px',
                          border: '1px solid rgba(0, 0, 0, 0.23)',
                          transition: 'border-color 0.2s',
                          '&:hover': {
                            borderColor: 'rgba(0, 0, 0, 0.87)',
                          },
                          '&:focus': {
                            outline: 'none',
                            borderColor: '#1976d2',
                            borderWidth: '2px',
                            padding: '15.5px 13px',
                          },
                        },
                        '& select.form-control': {
                          cursor: 'pointer',
                          backgroundColor: 'white',
                        },
                        '& textarea.form-control': {
                          fontFamily: 'inherit',
                          resize: 'vertical',
                          minHeight: '100px',
                        },
                        '& .checkbox label, & .radio label': {
                          fontWeight: 400,
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                        },
                        '& .checkbox input[type="checkbox"], & .radio input[type="radio"]': {
                          marginRight: '8px',
                          cursor: 'pointer',
                          width: '18px',
                          height: '18px',
                        },
                        '& .help-block': {
                          fontSize: '0.75rem',
                          color: 'rgba(0, 0, 0, 0.6)',
                          marginTop: '4px',
                        },
                        '& .has-error .form-control': {
                          borderColor: '#d32f2f',
                        },
                        '& .has-error label': {
                          color: '#d32f2f',
                        },
                        '& .error': {
                          color: '#d32f2f',
                          fontSize: '0.75rem',
                          marginTop: '4px',
                        },
                        '& .required::after': {
                          content: '" *"',
                          color: '#d32f2f',
                        },
                      }}
                    >
                      <Form
                        form={miniSchema}
                        submission={{ data: taskData || {} }}
                        onSubmit={handleSubmit}
                        onChange={(submission: any) => setCurrentFormData(submission.data)}
                        options={{
                          readOnly: submitting,
                          noAlerts: false,
                          buttonSettings: { showSubmit: false, showCancel: false },
                          fileUpload: {
                            apiBaseUrl: process.env.REACT_APP_API_URL ?? 'http://localhost:3000',
                            authToken: '',
                            taskId: taskId || ''
                          }
                        }}
                      />
                    </Box>
                  );
                }
              });
            })()}

            {/* Validation error display */}
            {validationError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {validationError}
              </Alert>
            )}

            {/* Bottom button bar matching iParts layout */}
            {/* Left: Save & Close, Cancel (secondary) | Right: Action buttons (primary) */}
            <Box
              sx={{
                mt: 3,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap'
              }}
            >
              {/* Secondary buttons on the left */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleSaveAndClose}
                  disabled={submitting}
                >
                  Save &amp; Close
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleCancel}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </Box>

              {/* Action buttons on the right */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {actionButtons.length > 0 ? (
                  actionButtons
                    .sort((a, b) => a.order - b.order)
                    .map((button) => (
                      <Button
                        key={button.id}
                        variant="contained"
                        color={button.color}
                        onClick={() => handleActionButtonClick(button.value)}
                        disabled={submitting}
                      >
                        {button.label}
                      </Button>
                    ))
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ alignSelf: 'center' }}>
                    (No action buttons configured)
                  </Typography>
                )}
              </Box>
            </Box>
          </>
        )}

        {submitting && (
          <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default FormRenderer;
